import React, {useState, useEffect, useCallback} from 'react';
import qs from 'query-string';
import {FlexColumn, FlexContainer, Heading, Text, TextField} from '@forecast-it/design-system';
import {GoogleOAuthProvider} from '@react-oauth/google';
import Logo, {LOGO_SIZE} from '../forecast-app/shared/components/logo/Logo';
import {Content, Page, PageContent} from './sage-signup-flow/Foreast.styled';
import {WideButton} from './sage-signup-flow/Sage.styled';
import {ErrorText, Form, FullWidthField, SsoRow, SsoIconButton, SwitchAccountButton} from './McpAuthorize.styled';
import GoogleLoginButton from './google-login-button';
import GoogleLoginMutation from '../mutations/google_login_mutation';
import CreateOktaVerificationKeyMutation from '../mutations/create_okta_verification_key_mutation';
import CreateOneloginOauthKeyMutation from '../mutations/create_onelogin_oauth_key_mutation';
import CreateAADOauthKeyMutation from '../mutations/create_AAD_oauth_key_mutation';
import {isInValidEmailFormat} from '../forecast-app/shared/util/EmailUtil';
import oktaLogo from '../images/integrations/okta-logo.png';
import microsoftEntraLogo from '../images/integrations/microsoft-entra-id-logo.svg';
import newGoogleLogo from '../images/sections/login/newGoogleLogo.png';
import newOneloginLogo from '../images/sections/login/oneLoginLogo.png';
import {XeroIcon} from '@/web-components';
import Util from '../forecast-app/shared/util/util';

const PHASE = {CHECKING: 'checking', LOGIN: 'login', CONSENT: 'consent'};

function authServerUrl() {
	return process.env.MCP_OAUTH_SERVER_URL || '';
}

function getRequestId() {
	return qs.parse(window.location.search).request_id || '';
}

const ConsentView = ({requestId, user, clientName, onSwitchAccount}) => {
	const [error, setError] = useState('');
	const [busy, setBusy] = useState(false);

	const respond = async approved => {
		setError('');
		setBusy(true);
		try {
			const resp = await fetch(`${authServerUrl()}/consent`, {
				method: 'POST',
				headers: {'Content-Type': 'application/json'},
				credentials: 'omit',
				body: JSON.stringify({request_id: requestId, approved}),
			});
			const data = await resp.json();
			if (!resp.ok || !data.redirect) {
				setError('This authorization link is invalid or has expired.');
				return;
			}
			window.location.assign(data.redirect);
		} catch (e) {
			setError('Something went wrong. Please try again.');
		} finally {
			setBusy(false);
		}
	};

	const who = user ? `${user.firstName} ${user.lastName}`.trim() || user.email : '';
	return (
		<Form>
			<Heading size="3">Authorize access</Heading>
			<Text>
				Grant <b>{clientName}</b> access to your Forecast account
				{who ? ` as ${who}` : ''}
				{user && user.companyName ? ` (${user.companyName})` : ''}.
			</Text>
			{error ? <ErrorText>{error}</ErrorText> : null}
			<FlexColumn gap="xs">
				<WideButton onPress={busy ? undefined : () => respond(true)}>Authorize</WideButton>
				<WideButton emphasis="low" onPress={busy ? undefined : () => respond(false)}>
					Deny
				</WideButton>
				{/* The page signs in with whatever Forecast session this browser holds, which may not be the
				    account the person means to connect. Signing in again replaces it on this request. */}
				<SwitchAccountButton emphasis="low" onPress={busy ? undefined : onSwitchAccount}>
					{who ? `Not ${who}? Use a different account` : 'Use a different account'}
				</SwitchAccountButton>
			</FlexColumn>
		</Form>
	);
};

const McpAuthorize = () => {
	const requestId = getRequestId();
	const [phase, setPhase] = useState(PHASE.CHECKING);
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [user, setUser] = useState(null);
	const [clientName, setClientName] = useState('');
	const [error, setError] = useState('');
	const [busy, setBusy] = useState(false);

	const detectSession = useCallback(async () => {
		if (!requestId) return false;
		try {
			const resp = await fetch(`${authServerUrl()}/session?request_id=${encodeURIComponent(requestId)}`, {
				method: 'GET',
				credentials: 'include',
			});
			const data = await resp.json();
			if (resp.ok && data.ok) {
				setUser(data.user);
				setClientName((data.client && data.client.name) || 'the application');
				setPhase(PHASE.CONSENT);
				return true;
			}
		} catch (e) {
			// fall through to login
		}
		return false;
	}, [requestId]);

	useEffect(() => {
		let active = true;
		(async () => {
			const found = await detectSession();
			if (active && !found) setPhase(PHASE.LOGIN);
		})();
		return () => {
			active = false;
		};
	}, [detectSession]);

	const onGoogle = tokenResponse => {
		setError('');
		setBusy(true);
		Util.CommitMutation(
			GoogleLoginMutation,
			{accessToken: tokenResponse.access_token},
			async ({googleLogin}) => {
				if (googleLogin && googleLogin.errors && googleLogin.errors.length) {
					setError('Google sign-in failed.');
					setBusy(false);
					return;
				}
				const found = await detectSession(); // session cookie is now set
				if (!found) setError('Could not complete sign-in. Please try again.');
				setBusy(false);
			},
			true,
			null,
			() => {
				setError('Google sign-in failed.');
				setBusy(false);
			}
		);
	};

	const buildMcpState = key => encodeURIComponent(`mcp:${requestId}:${key || ''}`);

	// Returns a 0-arg click handler that runs the tenant-resolving mutation with
	// the entered email, then hands the payload to `onSuccess` to build the
	// provider redirect. Shared email/request_id guard + busy handling.
	const runSso = (mutation, onSuccess) => () => {
		setError('');
		if (!requestId) {
			setError('This authorization link is invalid or has expired.');
			return;
		}
		if (!email || !isInValidEmailFormat(email)) {
			setError('Enter a valid email to continue.');
			return;
		}
		setBusy(true);
		Util.CommitMutation(
			mutation,
			{email},
			payload => {
				setBusy(false);
				onSuccess(payload);
			},
			true,
			null,
			() => {
				setBusy(false);
				setError('Sign-in failed. Please try again.');
			}
		);
	};

	const onOkta = runSso(CreateOktaVerificationKeyMutation, ({createOktaVerificationKey}) => {
		if (!createOktaVerificationKey || createOktaVerificationKey.key === null) {
			setError('SSO is not configured for this email.');
			return;
		}
		const {oktaUrl, oktaClientId, oktaDomainSuffix, key} = createOktaVerificationKey;
		const domainSuffix = oktaDomainSuffix || '.okta.com';
		window.location.assign(
			`https://${oktaUrl}${domainSuffix}/oauth2/v1/authorize?client_id=${oktaClientId}&response_type=code&scope=openid email profile&redirect_uri=${
				process.env.OKTA_REDIRECT_URI
			}&state=${buildMcpState(key)}`
		);
	});

	const onOnelogin = runSso(CreateOneloginOauthKeyMutation, ({createOneloginOauthKey}) => {
		if (!createOneloginOauthKey || createOneloginOauthKey.key === null) {
			setError('SSO is not configured for this email.');
			return;
		}
		const {oneloginUrl, oneloginClientId, key} = createOneloginOauthKey;
		window.location.assign(
			`https://${oneloginUrl}.onelogin.com/oidc/2/auth?client_id=${oneloginClientId}&redirect_uri=${
				process.env.ONELOGIN_REDIRECT_URI
			}&response_type=code&scope=openid&state=${buildMcpState(key)}`
		);
	});

	const onAzure = runSso(CreateAADOauthKeyMutation, ({createAADOauthKey}) => {
		if (!createAADOauthKey || createAADOauthKey.key === null) {
			setError('SSO is not configured for this email.');
			return;
		}
		const {AADTenant, AADClientId, key} = createAADOauthKey;
		window.location.assign(
			`https://login.microsoftonline.com/${AADTenant}.onmicrosoft.com/oauth2/v2.0/authorize?client_id=${AADClientId}&response_type=code&redirect_uri=${
				process.env.AAD_REDIRECT_URI
			}&response_mode=form_post&scope=openid email&state=${buildMcpState(key)}`
		);
	});

	const onXero = () => {
		setError('');
		if (!requestId) {
			setError('This authorization link is invalid or has expired.');
			return;
		}
		window.location.assign(
			`https://login.xero.com/identity/connect/authorize?response_type=code&client_id=${
				process.env.XERO_INTEGRATION_APP_CLIENT_ID
			}&redirect_uri=${process.env.XERO_SSO_REDIRECT_URI}&scope=openid email&state=${buildMcpState('')}`
		);
	};

	const switchAccount = () => {
		setUser(null);
		setClientName('');
		setError('');
		setPassword('');
		setPhase(PHASE.LOGIN);
	};

	const login = async () => {
		setError('');
		if (!requestId) {
			setError('This authorization link is invalid or has expired.');
			return;
		}
		setBusy(true);
		try {
			const resp = await fetch(`${authServerUrl()}/login`, {
				method: 'POST',
				headers: {'Content-Type': 'application/json'},
				credentials: 'omit',
				body: JSON.stringify({request_id: requestId, email, password}),
			});
			const data = await resp.json();
			if (!resp.ok || !data.ok) {
				setError(
					data.error === 'invalid_request'
						? 'This authorization link is invalid or has expired.'
						: 'Invalid email or password.'
				);
				return;
			}
			setUser(data.user);
			setClientName((data.client && data.client.name) || 'the application');
			setPhase(PHASE.CONSENT);
		} catch (e) {
			setError('Something went wrong. Please try again.');
		} finally {
			setBusy(false);
		}
	};

	return (
		<Page>
			<Content alignItems="center" justifyContent="center">
				<Logo size={LOGO_SIZE.MEDIUM} />
				<FlexContainer>
					<PageContent gap="xl">
						{phase === PHASE.CHECKING && (
							<Form>
								<Text>Checking your Forecast session…</Text>
							</Form>
						)}
						{phase === PHASE.LOGIN && (
							<GoogleOAuthProvider clientId="722419082843-j59h7vind9sucus9rv4ug9npk97rqffl.apps.googleusercontent.com">
								<Form>
									<Heading size="3">Sign in to Forecast</Heading>
									<FullWidthField>
										<TextField placeholder="Email" value={email} onValueChange={setEmail} />
									</FullWidthField>
									<FullWidthField>
										<TextField
											placeholder="Password"
											value={password}
											onValueChange={setPassword}
											isPasswordField
										/>
									</FullWidthField>
									{error ? <ErrorText>{error}</ErrorText> : null}
									<WideButton onPress={busy ? undefined : login}>Log in</WideButton>
									<SsoRow>
										<SsoIconButton
											as={GoogleLoginButton}
											type="button"
											title="Google"
											onSuccess={onGoogle}
											onFailure={() => setError('Google sign-in failed.')}
										>
											<img src={newGoogleLogo} alt="Google" />
										</SsoIconButton>
										<SsoIconButton type="button" title="OneLogin" onClick={busy ? undefined : onOnelogin}>
											<img src={newOneloginLogo} alt="OneLogin" />
										</SsoIconButton>
										<SsoIconButton type="button" title="Okta" onClick={busy ? undefined : onOkta}>
											<img src={oktaLogo} alt="Okta" />
										</SsoIconButton>
										<SsoIconButton
											type="button"
											title="Microsoft Entra ID"
											onClick={busy ? undefined : onAzure}
										>
											<img src={microsoftEntraLogo} alt="Microsoft Entra ID" />
										</SsoIconButton>
										<SsoIconButton type="button" title="Xero" onClick={busy ? undefined : onXero}>
											<XeroIcon />
										</SsoIconButton>
									</SsoRow>
								</Form>
							</GoogleOAuthProvider>
						)}
						{phase === PHASE.CONSENT && (
							<ConsentView
								requestId={requestId}
								user={user}
								clientName={clientName}
								onSwitchAccount={switchAccount}
							/>
						)}
					</PageContent>
				</FlexContainer>
			</Content>
		</Page>
	);
};

export default McpAuthorize;
export {authServerUrl, getRequestId};
